import Vue from 'vue';
import {
  API_ENDPOINTS,
  TB_DETAILS_PAGE,
  TB_LOAD_MESSAGES,
  TB_PAGINATED,
  TB_PAGINATED_FIELDS,
  TB_PAGINATED_MESSAGES,
  TB_PROGRESSIVE_LOAD,
  TB_UPLOAD,
  TB_UPLOAD_MESSAGES,
  TASKLIST_LOAD_STATUS,
} from '../utils/constants';
import {
  emptyTbUpload,
  mergeTbUpload,
  tbUploadFromDetails,
  tbUploadFromStart,
  normalizeTbUpload,
} from '../utils/tbUpload';
import { getApiErrorMessage } from '../utils/apiErrorUtils';
import {
  addPageToBuffer,
  buildRemainingPagePlan,
  createPageBuffer,
  isCurrentLoad,
  missingPages,
  runPagePlan,
} from '../utils/tasklistProgressiveLoad';

function getAxios() {
  return Vue.axios;
}

/** @type {AbortController|null} */
let tbLoadController = null;
/** @type {number|null} */
let tbUploadPollTimer = null;
/** @type {string|null} */
let tbUploadPollCycleId = null;
/** @type {(() => void)|null} */
let tbUploadVisibilityHandler = null;
let tbUploadStatusRequest = 0;
/** True while a status read is waiting. A tick must not start another read. */
let tbUploadStatusInFlight = false;
/**
 * Bumped when the open cycle changes. bulkAddTb snapshots it so a late
 * response cannot store cycle A's job onto cycle B.
 */
let tbUploadEpoch = 0;
/** @type {{ token: object, plan: object[], buffer: object }|null} */
let tbRetryState = null;

function isRequestCanceled(err) {
  return err?.name === 'AbortError' || err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED';
}

function resolveClientId(rootState) {
  const raw = rootState?.client || rootState?.user?.client || '';
  if (raw && typeof raw === 'object') return raw._id || raw.id || '';
  return raw || '';
}

function clearTbUploadPoll() {
  if (tbUploadPollTimer) {
    clearInterval(tbUploadPollTimer);
    tbUploadPollTimer = null;
  }
  if (tbUploadVisibilityHandler && typeof document !== 'undefined') {
    document.removeEventListener('visibilitychange', tbUploadVisibilityHandler);
  }
  tbUploadVisibilityHandler = null;
  tbUploadPollCycleId = null;
}

function abortTbRequests() {
  if (tbLoadController) {
    tbLoadController.abort();
    tbLoadController = null;
  }
  tbRetryState = null;
}

function beginTbLoad(commit, state, cycleId) {
  abortTbRequests();
  const generation = (state.tbLoad.generation || 0) + 1;
  commit('SET_TB_LOAD', {
    cycleId,
    generation,
    status: TASKLIST_LOAD_STATUS.LOADING,
    loaded: 0,
    total: 0,
    error: null,
    retrying: false,
  });
  tbLoadController = new AbortController();
  return { cycleId, generation, signal: tbLoadController.signal };
}

function tbPageBody(entry) {
  return {
    omitLocationDetail: true,
    pagination: { offset: entry.offset, limit: entry.limit },
  };
}

function emptyTbPage(loading = false) {
  return {
    records: [],
    totalCount: 0,
    page: 1,
    limit: TB_PAGINATED.PAGE_SIZE,
    totalPages: 0,
    appliedFilters: null,
    appliedSort: null,
    defaultFilters: null,
    defaultSort: null,
    defaultHeaders: null,
    loading,
  };
}

function tbPageFromPayload(payload, loading = false) {
  const data = payload || {};
  return {
    records: Array.isArray(data.records) ? data.records : [],
    totalCount: Number(data.totalCount) || 0,
    page: Number(data.page) || 1,
    limit: Number(data.limit) || TB_PAGINATED.PAGE_SIZE,
    totalPages: Number(data.totalPages) || 0,
    appliedFilters: data.appliedFilters == null ? null : data.appliedFilters,
    appliedSort: data.appliedSort == null ? null : data.appliedSort,
    defaultFilters: data.defaultFilters == null ? null : data.defaultFilters,
    defaultSort: data.defaultSort == null ? null : data.defaultSort,
    defaultHeaders: Array.isArray(data.defaultHeaders) ? data.defaultHeaders : null,
    loading,
  };
}

function mapFilterGroups(filters) {
  const groups = filters && Array.isArray(filters.groups) ? filters.groups : [];
  return groups.map((group) => {
    const next = {
      connector: group && group.connector,
      conditions: (group && Array.isArray(group.conditions) ? group.conditions : []).map((condition) => {
        const row = {
          column: condition && condition.column,
          operator: condition && condition.operator,
          value: condition ? condition.value : undefined,
        };
        if (condition && condition.id != null) row.id = condition.id;
        return row;
      }),
    };
    if (group && group.id != null) next.id = group.id;
    if (group && group.name) next.name = group.name;
    return next;
  });
}

function filtersForTbRequest(filters, { keepEmpty = false } = {}) {
  const groups = mapFilterGroups(filters);
  if (!groups.length && !keepEmpty) return null;
  const body = { groups };
  if (filters && Array.isArray(filters.group_connectors)) {
    body.group_connectors = filters.group_connectors.slice();
  }
  return body;
}

function sortForTbRequest(sort, { keepEmpty = false } = {}) {
  const items = [];
  (Array.isArray(sort) ? sort : []).forEach((item) => {
    if (!item || !item.field) return;
    items.push({
      field: item.field,
      direction: item.direction === 'desc' ? 'desc' : 'asc',
    });
  });
  if (!items.length && !keepEmpty) return null;
  return items;
}

/**
 * gettrialbalance body. useSavedView asks the server to apply the saved view
 * and must not also send filters or sort.
 * filterType is the filter tab that last applied these filters.
 * A sort apply keeps that value. Refresh, paging, and a sort with no prior filter apply omit it.
 */
function tbPagedBody({ page, filters, sort, useSavedView, paginate, filterType }) {
  const body = {};
  if (paginate) {
    body.pagination = { page: Number(page) || 1, limit: TB_PAGINATED.PAGE_SIZE };
  }
  if (useSavedView) {
    body.page = TB_PAGINATED.VIEW_PAGE;
    return body;
  }
  const requestFilters = filtersForTbRequest(filters);
  const requestSort = sortForTbRequest(sort);
  if (requestFilters) {
    body.filters = requestFilters;
    if (filterType === 'saved' || filterType === 'playground' || filterType === 'default' || filterType === 'applied') {
      body.filters = { ...requestFilters, type: filterType };
    }
  }
  if (requestSort) body.sort = requestSort;
  return body;
}

async function postTrialBalance(cycleId, body) {
  const response = await getAxios().post(`${API_ENDPOINTS.V3_GET_TRIAL_BALANCE}${cycleId}`, body);
  const dt = response?.data;
  if (dt?.status !== 'success' || dt.data == null) {
    throw new Error(dt?.message || TB_PAGINATED_MESSAGES.FETCH_PAGE_FAILED);
  }
  return dt.data;
}

/** Error JSON stuffed in a blob is small. A real workbook is not. */
const EXPORT_ERROR_BLOB_MAX = 65536;

function filenameFromContentDisposition(header, fallback) {
  if (!header) return fallback;
  const rfc5987 = header.match(/filename\*=(?:UTF-8'')?([^;]+)/i);
  if (rfc5987 && rfc5987[1]) {
    try {
      return decodeURIComponent(rfc5987[1].trim());
    } catch (_err) { /* keep fallback */ }
  }
  const standard = header.match(/filename=([^;]+)/i);
  if (standard && standard[1]) return standard[1].trim().replace(/^["']|["']$/g, '');
  return fallback;
}

/**
 * Save the workbook with a normal browser download.
 * The response blob keeps the spreadsheet Content-Type. Chrome opens Save As
 * for that type. A generic blob plus the download attribute goes to the
 * default Downloads folder.
 */
function triggerBrowserDownload(blob, filename) {
  const name = filename || TB_PAGINATED.EXPORT_FILENAME;
  const file = new Blob([blob], { type: 'application/octet-stream' });
  const url = window.URL.createObjectURL(file);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', name);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}

/** responseType blob hides JSON errors. Read message when the body is JSON or a small non-200 payload. */
async function messageFromErrorBlob(blob) {
  if (!blob || typeof blob.text !== 'function') return '';
  try {
    const text = await blob.text();
    if (!text) return '';
    const payload = JSON.parse(text);
    return payload?.error?.message || payload?.message || '';
  } catch (_err) {
    return '';
  }
}

const TB_EXPORT_FIELD_KEYS = new Set(TB_PAGINATED_FIELDS.map((field) => field.key));

/** Workbook columns. indexno, action, and any other display-only header are dropped. */
function exportColumnsBody(columns) {
  return (Array.isArray(columns) ? columns : [])
    .filter((column) => column && TB_EXPORT_FIELD_KEYS.has(column.field))
    .map((column) => ({
      field: column.field,
      label: column.label == null ? '' : String(column.label),
    }));
}

/** Latest paginated read. A slower earlier page must not overwrite a newer one. */
let tbPageRequest = 0;
/** Latest metadata read. Independent of the progressive tbLoad generation. */
let tbMetaRequest = 0;
/** Latest ERP status read. A cycle change must not paint the previous cycle's last sync. */
let tbStatusRequest = 0;
/** Latest location task-list read. A slower location must not replace the one now open. */
let locationTaskRequest = 0;

function locationNameMap(locations) {
  const map = {};
  (Array.isArray(locations) ? locations : []).forEach((loc) => {
    if (!loc || loc._id == null) return;
    map[String(loc._id)] = loc.name || '';
  });
  return map;
}

async function fetchTbPageRows(cycleId, entry, signal) {
  const response = await getAxios().post(
    `/v3/financialclose/analysis/gettrialbalance/${cycleId}`,
    tbPageBody(entry),
    { signal },
  );
  const dt = response?.data;
  if (dt?.status !== 'success' || !dt.data) {
    throw new Error(dt?.message || 'Failed to fetch trial balance');
  }
  return {
    rows: dt.data.records || [],
    totalCount: dt.data.totalCount,
  };
}

function startRemainingTbPages({ commit, state }, { token, plan, pages, buffer }) {
  const signal = token.signal;
  const isCurrent = () => !signal.aborted && isCurrentLoad(state.tbLoad, token);
  commit('SET_TB_LOAD', { status: TASKLIST_LOAD_STATUS.LOADING, error: null });

  const promise = runPagePlan({
    pages,
    concurrency: pages.length,
    isCurrent,
    fetchPage: (entry) => fetchTbPageRows(token.cycleId, entry, signal),
    onPage: (entry, result) => {
      const ready = addPageToBuffer(buffer, entry.index, result.rows);
      if (!ready.length) return;
      commit('APPEND_TB', { token, rows: ready });
    },
  }).then((outcome) => {
    if (outcome.status === 'stale' || !isCurrent()) return outcome;
    if (outcome.status === 'failed') {
      console.error('[trialBalance]: page failed', outcome.failedPage, outcome.error);
      tbRetryState = { token, plan, buffer };
      commit('SET_TB_LOAD', {
        status: TASKLIST_LOAD_STATUS.ERROR,
        error: getApiErrorMessage(outcome.error, TB_LOAD_MESSAGES.PAGE_FAILED),
        retrying: false,
      });
      return outcome;
    }
    tbRetryState = null;
    const held = (state.tb || []).length;
    commit('SET_TB_LOAD', {
      status: TASKLIST_LOAD_STATUS.COMPLETE,
      loaded: held,
      total: held,
      error: null,
      retrying: false,
    });
    return outcome;
  });

  return promise;
}

function getNova() {
  return Vue.prototype.$nova;
}

export default {
  namespaced: true,

  state: {
    metadata: {},
    tb: [],
    checklist: [],
    reflist: [],
    summary: {},
    pbcdata: {},
    formdata: {},
    userlist: {},
    userNameList: [],
    lastRunData: [],
    erpRunningStatus: false,
    locationtaskList: [],
    loading: false,
    locationNameById: {},
    tbPage: emptyTbPage(false),
    tbLoad: {
      cycleId: null,
      generation: 0,
      status: TASKLIST_LOAD_STATUS.IDLE,
      loaded: 0,
      total: 0,
      error: null,
      retrying: false,
    },
    tbUpload: emptyTbUpload(),
  },

  getters: {
    metadata: state => state.metadata,
    tb: state => state.tb,
    checklist: state => state.checklist,
    reflist: state => state.reflist,
    summary: state => state.summary,
    pbcdata: state => state.pbcdata,
    formdata: state => state.formdata,
    userlist: state => state.userlist,
    userNameList: state => state.userNameList,
    lastRunData: state => state.lastRunData,
    erpRunningStatus: state => state.erpRunningStatus,
    locationtaskList: state => state.locationtaskList,
    loading: state => state.loading,
    locationNameById: state => state.locationNameById,
    tbPage: state => state.tbPage,
    tbLoad: state => state.tbLoad,
    tbUpload: state => state.tbUpload,
  },

  mutations: {
    SET_METADATA(state, payload) { state.metadata = payload; },
    SET_TB(state, payload) { state.tb = payload; },
    SET_CHECKLIST(state, payload) { state.checklist = payload; },
    SET_REFLIST(state, payload) { state.reflist = payload; },
    SET_SUMMARY(state, payload) { state.summary = payload; },
    SET_PBCDATA(state, payload) { state.pbcdata = payload; },
    SET_FORMDATA(state, payload) { state.formdata = payload; },
    SET_USERLIST(state, payload) { state.userlist = payload; },
    SET_USERNAME_LIST(state, payload) { state.userNameList = payload; },
    SET_LAST_RUN_DATA(state, payload) { state.lastRunData = payload; },
    SET_ERP_RUNNING_STATUS(state, payload) { state.erpRunningStatus = payload; },
    SET_LOCATION_TASK_LIST(state, payload) { state.locationtaskList = payload; },
    SET_LOADING(state, payload) { state.loading = payload; },
    SET_LOCATION_NAME_BY_ID(state, payload) { state.locationNameById = payload || {}; },
    SET_TB_PAGE(state, payload) { state.tbPage = payload || emptyTbPage(false); },
    SET_TB_PAGE_LOADING(state, loading) {
      state.tbPage = { ...state.tbPage, loading: !!loading };
    },
    SET_TB_LOAD(state, partial) {
      state.tbLoad = { ...state.tbLoad, ...partial };
    },
    SET_TB_UPLOAD(state, payload) {
      state.tbUpload = payload || emptyTbUpload();
    },
    APPEND_TB(state, { token, rows }) {
      if (!isCurrentLoad(state.tbLoad, token)) return;
      const next = state.tb.concat(Array.isArray(rows) ? rows : []);
      state.tb = next;
      state.tbLoad = { ...state.tbLoad, loaded: next.length };
    },
  },

  actions: {
    async fetchDetails({ commit, state }, { id }) {
      const token = beginTbLoad(commit, state, id);
      const isCurrent = () => !token.signal.aborted && isCurrentLoad(state.tbLoad, token);
      commit('SET_LOADING', true);
      commit('SET_TB', []);
      commit('SET_METADATA', {});
      try {
        const [detailsRes, tbRes, formRes] = await Promise.all([
          getAxios().post(`/v3/financialclose/analysis/getdetails/${id}`, { page: 'tb' }, { signal: token.signal }),
          fetchTbPageRows(id, { offset: 0, limit: TB_PROGRESSIVE_LOAD.FIRST_PAGE_SIZE }, token.signal),
          getAxios().post('/v3/financialclose/analysis/getformdata', {}, { signal: token.signal }),
        ]);

        if (!isCurrent()) return false;

        if (detailsRes.data.status === 'success') {
          const detailsData = detailsRes.data.data || {};
          commit('SET_METADATA', detailsData.metadata || {});
          commit('SET_SUMMARY', detailsData.summary || {});
          const incomingUpload = tbUploadFromDetails(detailsData);
          if (incomingUpload) commit('SET_TB_UPLOAD', mergeTbUpload(state.tbUpload, incomingUpload));
        } else {
          throw new Error(detailsRes.data.message || 'Failed to fetch details');
        }

        const formData = formRes.data.status === 'success' ? (formRes.data.data || {}) : null;
        if (!formData) {
          throw new Error(formRes.data.message || 'Failed to fetch form data');
        }
        commit('SET_FORMDATA', formData);
        commit('SET_LOCATION_NAME_BY_ID', locationNameMap(formData.locations || formData.location));
        commit('SET_USERNAME_LIST', formData.users || []);
        const $nova = getNova();
        const userlist = {};
        const colors = $nova ? $nova.getColors((formData.users || []).length, 0.2) : [];
        for (let i = 0; i < (formData.users || []).length; i++) {
          const el = (formData.users || [])[i];
          userlist[el._id] = { ...el, color: colors[i] };
        }
        commit('SET_USERLIST', userlist);

        const firstRows = tbRes.rows || [];
        const totalCount = Math.max(Number(tbRes.totalCount) || 0, firstRows.length);
        commit('SET_TB', firstRows);
        const plan = buildRemainingPagePlan(totalCount, TB_PROGRESSIVE_LOAD);
        commit('SET_TB_LOAD', {
          loaded: firstRows.length,
          total: totalCount,
          status: plan.length ? TASKLIST_LOAD_STATUS.LOADING : TASKLIST_LOAD_STATUS.COMPLETE,
          error: null,
        });
        commit('SET_LOADING', false);
        if (plan.length && isCurrent()) {
          startRemainingTbPages({ commit, state }, {
            token,
            plan,
            pages: plan,
            buffer: createPageBuffer(firstRows),
          });
        }
        return true;
      } catch (error) {
        if (!isCurrent() || isRequestCanceled(error)) return false;
        console.error('[trialBalance/fetchDetails]:', error);
        commit('SET_TB_LOAD', {
          status: TASKLIST_LOAD_STATUS.ERROR,
          error: getApiErrorMessage(error, TB_LOAD_MESSAGES.PAGE_FAILED),
          retrying: false,
        });
        commit('sbError', error?.response?.data?.message || error?.message || 'Failed to fetch trial balance data', { root: true });
        return false;
      } finally {
        if (isCurrent()) commit('SET_LOADING', false);
      }
    },

    async retryTbPages({ commit, dispatch, state }, { id }) {
      const retry = tbRetryState;
      if (
        retry
        && String(retry.token.cycleId) === String(id)
        && isCurrentLoad(state.tbLoad, retry.token)
        && !retry.token.signal.aborted
      ) {
        tbRetryState = null;
        commit('SET_TB_LOAD', { retrying: true, error: null, status: TASKLIST_LOAD_STATUS.LOADING });
        const pages = missingPages(retry.plan, retry.buffer);
        try {
          await startRemainingTbPages({ commit, state }, { ...retry, pages });
        } finally {
          if (isCurrentLoad(state.tbLoad, retry.token)) {
            commit('SET_TB_LOAD', { retrying: false });
          }
        }
        return;
      }
      return dispatch('fetchDetails', { id });
    },

    cancelTbLoad({ commit, state }) {
      const generation = (state.tbLoad.generation || 0) + 1;
      abortTbRequests();
      commit('SET_TB_LOAD', {
        generation,
        status: TASKLIST_LOAD_STATUS.CANCELLED,
        retrying: false,
      });
    },

    /**
     * Fetch a single enriched task on demand (for task detail drawer).
     * Calls /v3/financialclose/analysis/gettask/:id/task/:taskId
     */
    async fetchTask(_, { projectId, taskId }) {
      try {
        const response = await getAxios().post(`/v3/financialclose/analysis/gettask/${projectId}/task/${taskId}`);
        const dt = response.data;
        if (dt.status !== 'success' || !dt.data) throw new Error('Failed to fetch task');
        return dt.data;
      } catch (err) {
        console.error('[trialBalance/fetchTask]:', err);
        throw err;
      }
    },

    /** Drop an in-flight ERP status read so a destroyed screen cannot commit a stale last sync. */
    cancelErpStatusRead() {
      tbStatusRequest += 1;
    },

    async fetchLastUpdatedAt({ commit, rootState }, { id, startDate, endDate }) {
      const requestId = ++tbStatusRequest;
      try {
        if (!rootState.erpEnabled?.tbErpEnabled) return;
        const body = { project: id, startDate, endDate, type: 'trialbalance' };
        const { data } = await getAxios().post('/v2/erp/get-status', body);
        if (requestId !== tbStatusRequest) return;
        if (data?.status === 'success') {
          commit('SET_LAST_RUN_DATA', data.data);
        }
      } catch (e) {
        if (requestId !== tbStatusRequest) return;
        console.error('[trialBalance/fetchLastUpdatedAt]: Failed to fetch ERP TB status', e);
      }
    },

    async stopErpPipeline({ commit, rootState }, { id, startDate, endDate }) {
      try {
        if (!rootState.erpEnabled?.tbErpEnabled) return;
        const body = { project: id, startDate, endDate, type: 'stop', processgroup: 'Skinlaundry-TB-pipeline' };
        const { data } = await getAxios().post('/v2/erp/check-status', body);
        if (data?.status === 'success') {
          commit('SET_ERP_RUNNING_STATUS', !!(data.data?.running));
        }
      } catch (e) {
        console.error('[trialBalance/stopErpPipeline]: Failed to stop ERP TB pipeline', e);
      }
    },

    async syncERP({ commit, state, rootState, dispatch }, { id, startDate, endDate }) {
      commit('SET_LOADING', true);
      try {
        if (!rootState.erpEnabled?.tbErpEnabled) return;
        if (state.erpRunningStatus) {
          await dispatch('stopErpPipeline', { id, startDate, endDate });
        }
        const dt = await getAxios().post('v2/erp/sync-trialbalance', { project: id, startDate, endDate });
        const msg = dt.data.message;
        if (dt.data.status === 'success') {
          commit('sbSuccess', msg || 'Sync successful!', { root: true });
        } else {
          commit('sbError', msg || 'Sync failed!', { root: true });
        }
      } catch (err) {
        console.error('[trialBalance/syncERP]:', err);
        commit('sbError', err?.message || err || 'Unknown error!', { root: true });
      } finally {
        commit('SET_LOADING', false);
      }
    },

    async fetchLocationTasklist({ commit }, { id, locationid }) {
      const request = ++locationTaskRequest;
      const isCurrent = () => request === locationTaskRequest;
      try {
        const dt = await getAxios().post(`v2/financialclose/analysis/getlocationbasedtasklist/${id}/c/${locationid}`);
        if (!isCurrent()) return false;
        if (dt.data.status === 'success') {
          const taskList = dt.data.data.map(item => {
            item.displayText = item.ref + ' - ' + item.description;
            return item;
          });
          commit('SET_LOCATION_TASK_LIST', taskList);
          return true;
        }
        throw new Error(dt.data.message);
      } catch (err) {
        if (!isCurrent()) return false;
        console.error('[trialBalance/fetchLocationTasklist]:', err);
        commit('SET_LOCATION_TASK_LIST', []);
        commit('sbError', err?.message || err || 'Unknown error!', { root: true });
        return false;
      }
    },

    async editTrialBalance({ commit }, { projectId, trialId, data }) {
      try {
        const dt = await getAxios().post(`/v2/financialclose/analysis/edittrialbalance/${projectId}/c/${trialId}`, { data });
        if (dt.data.status === 'success') {
          commit('sbSuccess', 'Update successful', { root: true });
          return true;
        } else {
          throw new Error(dt.data.message);
        }
      } catch (err) {
        console.error('[trialBalance/editTrialBalance]:', err);
        commit('sbError', err?.message || 'Unknown error!', { root: true });
        return false;
      }
    },

    /**
     * Cycle metadata and form data for the paginated screen.
     * Does not request trial-balance rows. The progressive fetchDetails path is unchanged.
     */
    async fetchTbMeta({ commit, state }, { id, reset }) {
      const requestId = ++tbMetaRequest;
      const isCurrent = () => requestId === tbMetaRequest;
      commit('SET_LOADING', true);
      if (reset) {
        tbUploadEpoch += 1;
        commit('SET_METADATA', {});
        commit('SET_TB_UPLOAD', emptyTbUpload());
      }
      try {
        const [detailsRes, formRes] = await Promise.all([
          getAxios().post(`${API_ENDPOINTS.V3_GET_ANALYSIS_DETAILS}${id}`, { page: TB_DETAILS_PAGE }),
          getAxios().post(API_ENDPOINTS.V3_GET_FORMDATA, {}),
        ]);
        if (!isCurrent()) return false;

        if (detailsRes.data.status === 'success') {
          const detailsData = detailsRes.data.data || {};
          commit('SET_METADATA', detailsData.metadata || {});
          commit('SET_SUMMARY', detailsData.summary || {});
          const incomingUpload = tbUploadFromDetails(detailsData);
          if (incomingUpload) commit('SET_TB_UPLOAD', mergeTbUpload(state.tbUpload, incomingUpload));
        } else {
          throw new Error(detailsRes.data.message || TB_PAGINATED_MESSAGES.FETCH_META_FAILED);
        }

        const formData = formRes.data.status === 'success' ? (formRes.data.data || {}) : null;
        if (!formData) {
          throw new Error(formRes.data.message || TB_PAGINATED_MESSAGES.FETCH_META_FAILED);
        }
        commit('SET_FORMDATA', formData);
        commit('SET_LOCATION_NAME_BY_ID', locationNameMap(formData.locations || formData.location));
        commit('SET_USERNAME_LIST', formData.users || []);
        const $nova = getNova();
        const userlist = {};
        const colors = $nova ? $nova.getColors((formData.users || []).length, 0.2) : [];
        for (let i = 0; i < (formData.users || []).length; i++) {
          const el = (formData.users || [])[i];
          userlist[el._id] = { ...el, color: colors[i] };
        }
        commit('SET_USERLIST', userlist);
        return true;
      } catch (error) {
        if (!isCurrent()) return false;
        console.error('[trialBalance/fetchTbMeta]:', error);
        commit('sbError', getApiErrorMessage(error, TB_PAGINATED_MESSAGES.FETCH_META_FAILED), { root: true });
        return false;
      } finally {
        if (isCurrent()) commit('SET_LOADING', false);
      }
    },

    /**
     * Start bulkaddtb. Validation failure stays HTTP 200 with valid: false.
     * 202 queues the job. 409 means another upload is already running.
     * A synchronous 200 success is the pre-async contract and still reloads.
     * A response that lands after the cycle epoch changed returns outcome stale
     * and does not store the job or start the poll.
     */
    async bulkAddTb({ commit, dispatch }, { projectId, data }) {
      const rows = Array.isArray(data) ? data : [];
      const epoch = tbUploadEpoch;
      const acceptUpload = () => epoch === tbUploadEpoch;
      try {
        const response = await getAxios().post(`${API_ENDPOINTS.BULK_ADD_TB}/${projectId}`, { data: rows });
        if (!acceptUpload()) return { outcome: 'stale' };
        const httpStatus = response?.status;
        const body = response?.data || {};
        const payload = body.data && typeof body.data === 'object' && !Array.isArray(body.data) ? body.data : {};
        if (body.status === 'failure' && payload.valid === false) {
          return {
            outcome: 'validation',
            message: body.message || TB_UPLOAD_MESSAGES.START_FAILED,
            rowErrors: Array.isArray(payload.rowErrors) ? payload.rowErrors : [],
          };
        }
        const queued = httpStatus === TB_UPLOAD.HTTP_ACCEPTED
          || payload.processingStatus === TB_UPLOAD.PROCESSING_STATUS_QUEUED
          || payload.tbUpload?.inProgress === true;
        if (queued) {
          const tbUpload = tbUploadFromStart(payload, rows.length);
          commit('SET_TB_UPLOAD', tbUpload);
          dispatch('startTbUploadPolling', { id: projectId });
          console.log('[trialBalance/bulkAddTb]:', { queueId: tbUpload.queueId, stage: tbUpload.stage });
          return { outcome: 'started', tbUpload };
        }
        if (body.status === 'success') {
          return { outcome: 'completed', message: body.message };
        }
        const message = body.message || TB_UPLOAD_MESSAGES.START_FAILED;
        commit('sbError', message, { root: true });
        return { outcome: 'error', message };
      } catch (error) {
        if (!acceptUpload()) return { outcome: 'stale' };
        const httpStatus = error?.response?.status;
        const body = error?.response?.data || {};
        const payload = body.data && typeof body.data === 'object' && !Array.isArray(body.data) ? body.data : {};
        if (httpStatus === TB_UPLOAD.HTTP_CONFLICT) {
          const normalized = normalizeTbUpload(payload.tbUpload || { inProgress: true });
          const tbUpload = normalized.inProgress ? normalized : { ...normalized, inProgress: true };
          commit('SET_TB_UPLOAD', tbUpload);
          dispatch('startTbUploadPolling', { id: projectId });
          const message = body.message || TB_UPLOAD_MESSAGES.ALREADY_RUNNING;
          commit('sbWarning', message, { root: true });
          console.log('[trialBalance/bulkAddTb]:', { conflict: true, queueId: tbUpload.queueId });
          return { outcome: 'conflict', message, tbUpload };
        }
        console.error('[trialBalance/bulkAddTb]:', error);
        const message = getApiErrorMessage(error, TB_UPLOAD_MESSAGES.START_FAILED);
        commit('sbError', message, { root: true });
        return { outcome: 'error', message };
      }
    },

    /** Cheap status read used by the upload poll. Does not toast. */
    async fetchTbUploadStatus({ commit, dispatch, rootState, state }, { id }) {
      const requestId = ++tbUploadStatusRequest;
      tbUploadStatusInFlight = true;
      const isCurrent = () => requestId === tbUploadStatusRequest && String(id) === String(tbUploadPollCycleId);
      try {
        const client = resolveClientId(rootState);
        const response = await getAxios().post(`${API_ENDPOINTS.TB_UPLOAD_STATUS}${id}`, { client });
        if (!isCurrent()) return null;
        const body = response?.data || {};
        if (body.status !== 'success') {
          throw new Error(body.message || TB_UPLOAD_MESSAGES.STATUS_FAILED);
        }
        const payload = body.data && typeof body.data === 'object' ? body.data : {};
        const next = mergeTbUpload(state.tbUpload, normalizeTbUpload(payload.tbUpload));
        if (!isCurrent()) return null;
        commit('SET_TB_UPLOAD', next);
        if (!next.inProgress) dispatch('stopTbUploadPolling');
        return next;
      } catch (error) {
        if (!isCurrent()) return null;
        console.error('[trialBalance/fetchTbUploadStatus]:', error);
        return null;
      } finally {
        tbUploadStatusInFlight = false;
      }
    },

    /**
     * One poll loop per cycle. Hidden tabs skip ticks; becoming visible polls once.
     * A second start for the same cycle does not add another interval.
     * A tick waits while a status read is already in flight so a slow response
     * is not invalidated by the next interval.
     */
    startTbUploadPolling({ dispatch }, { id } = {}) {
      if (!id) return;
      if (tbUploadPollTimer && String(tbUploadPollCycleId) === String(id)) return;
      clearTbUploadPoll();
      tbUploadPollCycleId = String(id);
      const tick = () => {
        if (typeof document !== 'undefined' && document.hidden) return;
        if (tbUploadStatusInFlight) return;
        dispatch('fetchTbUploadStatus', { id });
      };
      tick();
      tbUploadPollTimer = setInterval(tick, TB_UPLOAD.POLL_INTERVAL_MS);
      if (typeof document !== 'undefined') {
        tbUploadVisibilityHandler = () => {
          if (!document.hidden) tick();
        };
        document.addEventListener('visibilitychange', tbUploadVisibilityHandler);
      }
    },

    stopTbUploadPolling() {
      tbUploadStatusRequest += 1;
      clearTbUploadPoll();
    },

    /** Invalidate an in-flight bulkAddTb after the open cycle changes. */
    advanceTbUploadEpoch() {
      tbUploadEpoch += 1;
    },

    /**
     * One page of trial balance. useSavedView sends page fc_trialbalance and no filters or sort
     * so the server applies the saved view. Otherwise filters and sort are sent only when present.
     * reset replaces the current page (cycle change) instead of leaving the previous cycle's rows.
     */
    async fetchTbPage({ commit }, { id, page, filters, sort, useSavedView, reset, filterType }) {
      const requestId = ++tbPageRequest;
      const isCurrent = () => requestId === tbPageRequest;
      if (reset) {
        commit('SET_TB_PAGE', emptyTbPage(true));
      } else {
        commit('SET_TB_PAGE_LOADING', true);
      }
      try {
        const data = await postTrialBalance(id, tbPagedBody({
          page: page || 1,
          filters,
          sort,
          useSavedView: !!useSavedView,
          paginate: true,
          filterType,
        }));
        if (!isCurrent()) return null;
        const next = tbPageFromPayload(data, false);
        commit('SET_TB_PAGE', next);
        return next;
      } catch (error) {
        if (!isCurrent()) return null;
        console.error('[trialBalance/fetchTbPage]:', error);
        commit('SET_TB_PAGE_LOADING', false);
        commit('sbError', getApiErrorMessage(error, TB_PAGINATED_MESSAGES.FETCH_PAGE_FAILED), { root: true });
        return null;
      }
    },

    /**
     * Persist the saved-tab filter, sort, and/or column headers. Does not refetch.
     * headers is the visible field-key order, stored as USER_CUSTOM.custom_layout.
     * The following apply event loads the page with those filters explicitly.
     */
    async saveTbViewConfig({ commit }, payload) {
      const source = payload || {};
      const body = { page: TB_PAGINATED.VIEW_PAGE };
      if (Object.prototype.hasOwnProperty.call(source, 'filters')) {
        body.filters = filtersForTbRequest(source.filters, { keepEmpty: true });
      }
      if (Object.prototype.hasOwnProperty.call(source, 'sort')) {
        body.sort = sortForTbRequest(source.sort, { keepEmpty: true });
      }
      if (Object.prototype.hasOwnProperty.call(source, 'headers')) {
        body.headers = (Array.isArray(source.headers) ? source.headers : [])
          .filter((key) => typeof key === 'string' && key);
      }
      if (body.filters == null && body.sort == null && body.headers == null) return false;
      try {
        const response = await getAxios().post(API_ENDPOINTS.V3_SAVE_VIEW_CONFIG, body);
        if (response?.data?.status !== 'success') {
          throw new Error(response?.data?.message || TB_PAGINATED_MESSAGES.SAVE_FAILED);
        }
        commit('sbSuccess', response.data.message || TB_PAGINATED_MESSAGES.SAVED, { root: true });
        return true;
      } catch (error) {
        console.error('[trialBalance/saveTbViewConfig]:', error);
        commit('sbError', getApiErrorMessage(error, TB_PAGINATED_MESSAGES.SAVE_FAILED), { root: true });
        return false;
      }
    },

    /**
     * Server-built xlsx for the filtered set. columns are visible data fields in header order.
     * Success is a file blob. Failures may be JSON inside that blob.
     */
    async exportTrialBalance({ commit }, { id, filters, sort, columns }) {
      const body = { columns: exportColumnsBody(columns) };
      const requestFilters = filtersForTbRequest(filters);
      const requestSort = sortForTbRequest(sort);
      if (requestFilters) body.filters = requestFilters;
      if (requestSort) body.sort = requestSort;
      try {
        const response = await getAxios().post(
          `${API_ENDPOINTS.V3_EXPORT_TRIAL_BALANCE}${id}`,
          body,
          { responseType: 'blob' },
        );
        const blob = response?.data;
        const contentType = String(response?.headers?.['content-type'] || '');
        if (contentType.includes('json')) {
          const message = await messageFromErrorBlob(blob);
          throw new Error(message || TB_PAGINATED_MESSAGES.EXPORT_FAILED);
        }
        const filename = filenameFromContentDisposition(
          response?.headers?.['content-disposition'],
          TB_PAGINATED.EXPORT_FILENAME,
        );
        triggerBrowserDownload(blob instanceof Blob ? blob : new Blob([blob]), filename);
        return true;
      } catch (error) {
        console.error('[trialBalance/exportTrialBalance]:', error);
        const blob = error?.response?.data;
        const status = error?.response?.status;
        const contentType = String(error?.response?.headers?.['content-type'] || '');
        const smallError = status && status !== 200 && blob && blob.size != null && blob.size <= EXPORT_ERROR_BLOB_MAX;
        let message = '';
        if (contentType.includes('json') || smallError) {
          message = await messageFromErrorBlob(blob);
        }
        commit('sbError', message || getApiErrorMessage(error, TB_PAGINATED_MESSAGES.EXPORT_FAILED), { root: true });
        return false;
      }
    },
  },
};
